import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { PublicUnitForMenu } from '../components/detail/unit/types';

/**
 * Public-side equivalent of the agent's `topViewingFloorPlansToCompare`
 * state in `SuperPickerApp.tsx`. Holds up to 3 units marked for
 * floor-plan comparison across projects within one SPA session.
 *
 * State lives in `FloorPlanCompareProvider` (mounted at the public app
 * root) so the cart survives navigating back to `SitePlansPage` and
 * opening another project.
 */
export const FLOOR_PLAN_COMPARE_CAPACITY = 3;

/** Stable cart identity across projects (synthetic keys repeat per project). */
export const compareCartIdentity = (
  unit: Pick<PublicUnitForMenu, 'unitKey' | 'projectId' | 'projectName'>,
): string => {
  const project = (unit.projectId ?? unit.projectName ?? '').trim();
  return `${project}::${unit.unitKey}`;
};

export interface FloorPlanCompareApi {
  cart: PublicUnitForMenu[];
  capacity: number;
  compareOpen: boolean;
  openCompare: () => void;
  closeCompare: () => void;
  isInCart: (unitKey: string, projectId?: string | null) => boolean;
  /** Returns the new presence (`true` = added, `false` = removed/rejected). */
  toggle: (unit: PublicUnitForMenu) => boolean;
  remove: (unit: PublicUnitForMenu) => void;
  move: (fromIndex: number, toIndex: number) => void;
  clear: () => void;
}

export interface FloorPlanCompareContextValue extends FloorPlanCompareApi {}

export const FloorPlanCompareContext =
  createContext<FloorPlanCompareContextValue | null>(null);

export function useFloorPlanCompareState(): FloorPlanCompareContextValue {
  const [cart, setCart] = useState<PublicUnitForMenu[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const openCompare = useCallback(() => setCompareOpen(true), []);
  const closeCompare = useCallback(() => setCompareOpen(false), []);

  const isInCart = useCallback(
    (unitKey: string, projectId?: string | null) => {
      if (projectId != null && projectId !== '') {
        const id = compareCartIdentity({ unitKey, projectId, projectName: '' });
        return cart.some((u) => compareCartIdentity(u) === id);
      }
      return cart.some((u) => u.unitKey === unitKey);
    },
    [cart],
  );

  const toggle = useCallback((unit: PublicUnitForMenu): boolean => {
    let result = false;
    setCart((prev) => {
      const id = compareCartIdentity(unit);
      const idx = prev.findIndex((u) => compareCartIdentity(u) === id);
      if (idx >= 0) {
        result = false;
        const next = prev.slice();
        next.splice(idx, 1);
        return next;
      }
      // Mirror agent: only units with a floor plan can be compared.
      if (prev.length >= FLOOR_PLAN_COMPARE_CAPACITY || !unit.floorplan) {
        result = false;
        return prev;
      }
      result = true;
      return [...prev, unit];
    });
    return result;
  }, []);

  const remove = useCallback((unit: PublicUnitForMenu) => {
    const id = compareCartIdentity(unit);
    setCart((prev) => prev.filter((u) => compareCartIdentity(u) !== id));
  }, []);

  const move = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    setCart((prev) => {
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= prev.length || toIndex >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return next;
    });
  }, []);

  const clear = useCallback(() => setCart([]), []);

  return useMemo<FloorPlanCompareContextValue>(
    () => ({
      cart,
      capacity: FLOOR_PLAN_COMPARE_CAPACITY,
      compareOpen,
      openCompare,
      closeCompare,
      isInCart,
      toggle,
      remove,
      move,
      clear,
    }),
    [cart, compareOpen, openCompare, closeCompare, isInCart, toggle, remove, move, clear],
  );
}

export const FloorPlanCompareProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = useFloorPlanCompareState();
  return React.createElement(FloorPlanCompareContext.Provider, { value }, children);
};

export function useFloorPlanCompare(): FloorPlanCompareApi {
  const ctx = useContext(FloorPlanCompareContext);
  if (!ctx) {
    throw new Error('useFloorPlanCompare must be used within FloorPlanCompareProvider');
  }
  return ctx;
}
